/**
 * Cell colours for the cohort report (COHORT-REPORT).
 *
 * - Peer columns: red (worse than peers) → grey (median) → blue (better),
 *   the same scale as the ACEP 2026 comparison.
 * - Neutral columns (duration, EEG): light → dark grey by percentile, no verdict.
 * - Within-person EEG: purple (lower than usual for this person) → grey →
 *   orange (higher than usual); neither end is better or worse.
 */
import type { CohortColour } from '../../api/cohortReport';

type Rgb = [number, number, number];

const MID: Rgb = [232, 236, 241];
const WORSE: Rgb = [192, 57, 43];
const BETTER: Rgb = [37, 99, 168];
const LOW: Rgb = [241, 242, 244];
const HIGH: Rgb = [107, 114, 128];
const BELOW_USUAL: Rgb = [124, 58, 237];
const ABOVE_USUAL: Rgb = [217, 119, 6];

/** Deviation (log ratio) at which the within-person colour saturates. */
export const WITHIN_PERSON_SATURATION = 0.6;

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  const u = Math.max(0, Math.min(1, t));
  return [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * u)) as Rgb;
}

function css([r, g, b]: Rgb): string {
  return `rgb(${r}, ${g}, ${b})`;
}

/** Readable text on a fill: white on dark fills, near-black otherwise. */
function ink([r, g, b]: Rgb): string {
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum < 0.55 ? '#ffffff' : '#1f2733';
}

export interface CellPaint {
  background: string;
  color: string;
}

export function percentileFill(
  percentile: number | null | undefined,
  colour: CohortColour,
): CellPaint | null {
  if (percentile == null) return null;
  let rgb: Rgb;
  if (colour === 'neutral') {
    rgb = mix(LOW, HIGH, percentile / 100);
  } else {
    const t = (percentile - 50) / 50;
    rgb = t < 0 ? mix(MID, WORSE, -t) : mix(MID, BETTER, t);
  }
  return { background: css(rgb), color: ink(rgb) };
}

export function deviationFill(deviation: number | null | undefined): CellPaint | null {
  if (deviation == null) return null;
  const t = deviation / WITHIN_PERSON_SATURATION;
  const rgb = t < 0 ? mix(MID, BELOW_USUAL, -t) : mix(MID, ABOVE_USUAL, t);
  return { background: css(rgb), color: ink(rgb) };
}

/** Legend stops for the three scales, left → right. */
export const LEGENDS = {
  peer: [css(WORSE), css(MID), css(BETTER)],
  neutral: [css(LOW), css(HIGH)],
  within: [css(BELOW_USUAL), css(MID), css(ABOVE_USUAL)],
};
